import React, {
  createContext,
  FormEvent,
  FormHTMLAttributes,
  ReactNode,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { isValidEmail } from '../utils/email';

/**
 * In-house replacement for the unmaintained `react-material-ui-form-validator` package.
 * Provides the same public API used across the app:
 *
 *   <ValidatorForm onSubmit={...}>
 *     <TextValidator name="..." value={...} validators={['required', 'customRule']} errorMessages={[...]} />
 *   </ValidatorForm>
 *
 *   ValidatorForm.addValidationRule('customRule', (value) => boolean)
 *   ValidatorForm.removeValidationRule('customRule')
 *
 * Built on top of the project's current MUI TextField, so it stays compatible
 * with MUI v5+/React 18+ and needs no extra dependency.
 */

export type ValidationRuleFn = (value: unknown) => boolean | Promise<boolean>;

const customRules = new Map<string, ValidationRuleFn>();

const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

const builtInRules: Record<string, ValidationRuleFn> = {
  required: (value) => !isEmpty(value),
  // Delegates to the single shared email rule (common/utils/email).
  isEmail: (value) => isEmpty(value) || isValidEmail(String(value)),
  isNumber: (value) => isEmpty(value) || !isNaN(Number(String(value).trim())),
  trim: (value) => isEmpty(value) || String(value).trim().length > 0,
};

const resolveRule = (name: string): ValidationRuleFn | undefined => {
  if (customRules.has(name)) {
    return customRules.get(name);
  }
  const matchRegexp = name.match(/^matchRegexp:(.+)$/);
  if (matchRegexp) {
    const regexp = new RegExp(matchRegexp[1]);
    return (value) => isEmpty(value) || regexp.test(String(value));
  }
  const maxLength = name.match(/^maxStringLength:(\d+)$/);
  if (maxLength) {
    const max = Number(maxLength[1]);
    return (value) => isEmpty(value) || String(value).length <= max;
  }
  const minLength = name.match(/^minStringLength:(\d+)$/);
  if (minLength) {
    const min = Number(minLength[1]);
    return (value) => isEmpty(value) || String(value).length >= min;
  }
  return builtInRules[name];
};

interface FieldHandle {
  validate: () => Promise<boolean>;
  reset: () => void;
}

interface FormContextValue {
  register: (field: FieldHandle) => void;
  unregister: (field: FieldHandle) => void;
  instantValidate: boolean;
}

const FormContext = createContext<FormContextValue | null>(null);

export interface ValidatorFormProps extends Omit<
  FormHTMLAttributes<HTMLFormElement>,
  'onSubmit' | 'onError'
> {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onError?: () => void;
  instantValidate?: boolean;
  children?: ReactNode;
}

interface ValidatorFormComponent extends React.FC<ValidatorFormProps> {
  addValidationRule: (name: string, callback: ValidationRuleFn) => void;
  removeValidationRule: (name: string) => void;
  hasValidationRule: (name: string) => boolean;
  getValidationRule: (name: string) => ValidationRuleFn | undefined;
}

export const ValidatorForm: ValidatorFormComponent = ({
  onSubmit,
  onError,
  instantValidate = true,
  children,
  ...formProps
}) => {
  const fieldsRef = useRef<Set<FieldHandle>>(new Set());

  const contextValue = useMemo<FormContextValue>(
    () => ({
      register: (field) => {
        fieldsRef.current.add(field);
      },
      unregister: (field) => {
        fieldsRef.current.delete(field);
      },
      instantValidate,
    }),
    [instantValidate]
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const results = await Promise.all(
      Array.from(fieldsRef.current).map((field) => field.validate())
    );
    if (results.every(Boolean)) {
      onSubmit(event);
    } else if (onError) {
      onError();
    }
  };

  return (
    <FormContext value={contextValue}>
      <form noValidate {...formProps} onSubmit={handleSubmit}>
        {children}
      </form>
    </FormContext>
  );
};

ValidatorForm.addValidationRule = (name: string, callback: ValidationRuleFn) => {
  customRules.set(name, callback);
};

ValidatorForm.removeValidationRule = (name: string) => {
  customRules.delete(name);
};

ValidatorForm.hasValidationRule = (name: string) => customRules.has(name);

ValidatorForm.getValidationRule = (name: string) => customRules.get(name);

export type TextValidatorProps = TextFieldProps & {
  validators?: string[];
  errorMessages?: string | string[];
};

export const TextValidator: React.FC<TextValidatorProps> = ({
  validators = [],
  errorMessages = [],
  onChange,
  onBlur,
  error,
  helperText,
  value,
  ...rest
}) => {
  const form = use(FormContext);
  const [validationError, setValidationError] = useState<string | null>(null);
  const touchedRef = useRef(false);

  // Keep the latest props available to the stable field handle.
  // The ref is only read/written inside effects and event handlers — never during render.
  const latestRef = useRef({ value, validators, errorMessages });
  useEffect(() => {
    latestRef.current = { value, validators, errorMessages };
  });

  // Create the stable field handle exactly once via a lazy useState initializer.
  // This avoids the `if (!ref.current) { ref.current = ... }` pattern, which
  // reads and writes a ref during render.
  const [field] = useState(() => {
    const getErrorMessage = (index: number): string => {
      const { errorMessages: messages, validators: rules } = latestRef.current;
      if (typeof messages === 'string') {
        return messages;
      }
      // Fall back to the last provided message when fewer messages than validators exist.
      return (
        messages[index] ??
        messages[messages.length - 1] ??
        `Validation failed: ${rules[index] ?? ''}`
      );
    };

    const runValidation = async (valueToCheck: unknown): Promise<boolean> => {
      const { validators: rules } = latestRef.current;
      for (let i = 0; i < rules.length; i++) {
        const rule = resolveRule(rules[i]);
        if (!rule) {
          // Unknown rule names are ignored, matching the tolerant behavior of the old package.
          continue;
        }
        const valid = await rule(valueToCheck);
        if (!valid) {
          setValidationError(getErrorMessage(i));
          return false;
        }
      }
      setValidationError(null);
      return true;
    };

    const handle: FieldHandle = {
      validate: () => {
        touchedRef.current = true;
        return runValidation(latestRef.current.value);
      },
      reset: () => {
        touchedRef.current = false;
        setValidationError(null);
      },
    };

    return { handle, runValidation };
  });

  useEffect(() => {
    if (!form) {
      return undefined;
    }
    const { handle } = field;
    form.register(handle);
    return () => {
      form.unregister(handle);
    };
  }, [form, field]);

  const handleChange: TextFieldProps['onChange'] = (event) => {
    if (onChange) {
      onChange(event);
    }
    if ((form?.instantValidate ?? true) && touchedRef.current) {
      // Validate against the freshly typed value, since the controlled prop updates on the next render.
      void field.runValidation(event.target.value);
    }
  };

  const handleBlur: TextFieldProps['onBlur'] = (event) => {
    if (onBlur) {
      onBlur(event);
    }
    if (form?.instantValidate ?? true) {
      touchedRef.current = true;
      void field.handle.validate();
    }
  };

  return (
    <TextField
      {...rest}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(validationError) || Boolean(error)}
      helperText={validationError ?? helperText}
    />
  );
};

export default ValidatorForm;
